'use client'

import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'

import { GoogleLogoIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { useAuth, useAuthDialogControl } from '@/features/auth/hooks'
import { useGoogleLoginPopup } from '@/features/auth/hooks/use-google-login-popup'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'

export function GoogleLoginButton({
  className,
  mode,
  callbackUrl,
}: {
  className?: string
  mode: 'login' | 'signup'
  callbackUrl?: string
}) {
  const t = useTranslations('auth')
  const { googleLogin } = useAuth()
  const { loginWithGooglePopup } = useGoogleLoginPopup()
  const { isOpen, handleSuccess, closeDialog } = useAuthDialogControl()
  const router = useRouter()

  const handleGoogleLogin = async () => {
    try {
      const authState = await loginWithGooglePopup({
        redirectUrl: callbackUrl,
      })

      // 성공 시 다이얼로그를 확실히 닫고 성공 처리 수행
      // handleSuccess 내부에 대화 초기화 및 신규유저 온보딩 리다이렉트 로직이 포함되어 있습니다.
      handleSuccess(authState.isNewUser)
    } catch (error) {
      const err = error as Error

      if (err.message === 'POPUP_BLOCKED') {
        // 팝업 차단 시 기존 리다이렉트 방식으로 폴백
        toast.info(t('popupBlockedRedirecting'))
        googleLogin(callbackUrl)
        return
      }

      if (err.message === 'REQUIRES_2FA') {
        // The popup hook is already navigating to the MFA form.
        closeDialog()
        return
      }

      if (err.message === 'POPUP_CLOSED') {
        // 사용자가 팝업을 직접 닫은 경우 — 에러 아님
        return
      }

      toast.error(err.message || t('googleLoginFailed'))
    }
  }

  return (
    <Button
      type="button"
      className={cn(['bg-transparent', className])}
      variant={'outline'}
      onClick={async (e) => {
        e.preventDefault()
        await handleGoogleLogin()
      }}
    >
      <div className="flex items-center gap-2 font-sans text-sm font-medium tracking-tight">
        <GoogleLogoIcon size={16} />
        {t('signIn')}
      </div>
    </Button>
  )
}
